import { useState } from 'react'
import { FormRequestPart } from './aiAssistantModel'
import { LookupQuery, LookupResult } from './aiAssistantApi'
import { PlaceData } from '../common/googlePlacesAutoCompleteModel'
import FormFields from './FormFields'
import { historyCaptionStyle } from './historyStyles'
import {
  FormValues,
  addressAutofill,
  buildSubmitValues,
  clearDependentLookups,
  fieldErrors,
  initialFormValues,
  missingRequiredFields,
  submittedDisplayValue,
} from './formRequestModel'

type Props = {
  part: FormRequestPart
  interactive: boolean
  onSubmit: (formId: string, values: Record<string, unknown>) => void
  // Searches a lookup field's entity via the chat lookup endpoint. Optional so
  // forms without lookup fields need no wiring.
  onLookupSearch?: (query: LookupQuery) => Promise<LookupResult>
  onLookupCreateRequest?: (entity: string, query: string) => void
}

const panelStyle = {
  margin: '8px 0',
  border: '1px solid #d9d9d9',
  borderRadius: 8,
  padding: 12,
  background: '#f5f5f5',
}

/** A submitted form, replayed from history: what was asked, what was entered. */
const SubmittedForm = ({ part }: { part: FormRequestPart }) => {
  const values = (part.resolution ?? {}) as FormValues

  return (
    <div style={panelStyle}>
      <p style={{ margin: '0 0 12px', fontWeight: 500 }}>{part.prompt}</p>
      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', margin: 0 }}>
        {part.fields.map((field) => (
          <div key={field.name} style={{ display: 'contents' }}>
            <dt style={{ color: '#666' }}>{field.label}</dt>
            <dd style={{ margin: 0 }}>{submittedDisplayValue(field, values[field.name])}</dd>
          </div>
        ))}
      </dl>
      <p style={historyCaptionStyle}>Submitted</p>
    </div>
  )
}

/**
 * The standalone fill-in form: owns its values/touch state and validation,
 * and submits to the form's write tool. The field rendering lives in
 * FormFields so the question-set pager can reuse it with per-page state.
 */
const FormRequest = ({
  part,
  interactive,
  onSubmit,
  onLookupSearch,
  onLookupCreateRequest,
}: Props) => {
  const [values, setValues] = useState<FormValues>(() => initialFormValues(part.fields))
  const [touched, setTouched] = useState<Set<string>>(new Set())

  if (part.status === 'submitted') {
    return <SubmittedForm part={part} />
  }

  // No local "submitted" lock: the parent drives interactivity (off while the
  // request streams, and permanently once the write result comes back), so a
  // failed submit stays retryable. Double-submit during the request is already
  // prevented by the synchronous isStreamingRef guard in useChat.
  const disabled = !interactive
  const missing = missingRequiredFields(part.fields, values)
  const errors = fieldErrors(part.fields, values)
  const canSubmit = !disabled && missing.length === 0 && Object.keys(errors).length === 0

  const shownErrors: Record<string, string> = {}
  for (const name of Object.keys(errors)) {
    if (touched.has(name)) {
      shownErrors[name] = errors[name]
    }
  }

  const setValue = (name: string, value: unknown) =>
    setValues((prev) =>
      clearDependentLookups(part.fields, { ...prev, [name]: value }, name)
    )

  const markTouched = (name: string) =>
    setTouched((prev) => (prev.has(name) ? prev : new Set(prev).add(name)))

  const handlePlaceSelected = (addressFieldName: string, place: PlaceData) => {
    const filled = addressAutofill(part.fields, addressFieldName, place)
    setValues((prev) => ({ ...prev, ...filled }))
    setTouched((prev) => {
      const next = new Set(prev)
      Object.keys(filled).forEach((name) => next.add(name))
      return next
    })
  }

  const handleSubmit = () => {
    if (!canSubmit) {
      return
    }
    onSubmit(part.form_id, buildSubmitValues(part.fields, values))
  }

  return (
    <div style={panelStyle}>
      <p style={{ margin: '0 0 12px', fontWeight: 500 }}>{part.prompt}</p>
      <FormFields
        idPrefix={part.form_id}
        fields={part.fields}
        values={values}
        disabled={disabled}
        errors={shownErrors}
        onChange={setValue}
        onBlur={markTouched}
        onPlaceSelected={handlePlaceSelected}
        onLookupSearch={
          onLookupSearch
            ? (field, q, parentValue) =>
                onLookupSearch({
                  formId: part.form_id,
                  field,
                  q,
                  parentValue,
                })
            : undefined
        }
        onLookupCreateRequest={onLookupCreateRequest}
      />
      <button
        type="button"
        disabled={!canSubmit}
        onClick={handleSubmit}
        style={{
          marginTop: 12,
          padding: '6px 16px',
          borderRadius: 8,
          border: '1px solid #1677ff',
          background: canSubmit ? '#1677ff' : '#f5f5f5',
          color: canSubmit ? '#fff' : '#999',
          cursor: canSubmit ? 'pointer' : 'default',
          font: 'inherit',
        }}
      >
        {part.submit_label}
      </button>
      {/* Historical and never filled in: the user closed the tab on it. */}
      {part.status === 'pending' && <p style={historyCaptionStyle}>Not submitted.</p>}
    </div>
  )
}

export default FormRequest
